import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { HumanStepDefinition } from "../../../api/content/authoring/stepDecoders";
import type { ValueKind } from "../../../api/content/authoring/selectorDecoders";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS, FORM_HELP_TEXT_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import type { AuthoringOptions } from "../draft/authoringOptions";
import BilingualColumns from "../draft/BilingualColumns";
import { columnFallback, columnText, formLanguages, LANGUAGE_NAMES, storedTranslations, withChoiceKey, withColumnText } from "../draft/bilingualTexts";
import FieldProblems from "../draft/FieldProblems";
import type { DefinitionCheck } from "../draft/useDefinitionCheck";
import MarkdownField from "../../../ui/forms/MarkdownField";
import Section from "../../../ui/forms/Section";
import { choiceKeyFree, KEY_NAME_PATTERN, withChoiceRenamed, withHumanInput } from "./stepDefinitions";
import { NESTED_OPTIONS_CLASS, WIDE_CONTROL_ROW_CLASS } from "../shared/authoringStyles";
import OptionLabel from "../../../ui/forms/OptionLabel";
import Field from "../../../ui/forms/Field";

const INSTRUCTIONS = { field: "instructions" } as const;

interface HumanStepFieldsProps {
  step: HumanStepDefinition;
  options: AuthoringOptions;
  check: DefinitionCheck;
  onChange: (step: HumanStepDefinition) => void;
}

/** A human step (AU-4): instructions, the inputs shown to the participant, and the answer asked for. */
export default function HumanStepFields({ step, options, check, onChange }: HumanStepFieldsProps) {
  const { t, i18n } = useTranslation();
  const languages = formLanguages(i18n.resolvedLanguage);
  const catalogue = options.catalogue.status === "loaded" ? options.catalogue.value : null;
  const valueKinds: readonly ValueKind[] = catalogue?.valueKinds ?? [...new Set(Object.values(step.inputs).flatMap((slot) => slot.accepts))];
  const [newInput, setNewInput] = useState("");
  const inputNames = Object.keys(step.inputs);
  const response = step.response;

  return (
    <>
      <Section title={t("authoring.step.human.instructions")} hint={t("authoring.step.human.instructionsHint")}>
        <BilingualColumns languages={languages}>
          {(language, languageName) => (
            <>
              <MarkdownField
                label={t("authoring.bilingual.field", { field: t("authoring.step.human.instructions"), language: languageName })}
                lang={language}
                value={columnText(step.translations, language, INSTRUCTIONS, step.instructions)}
                placeholder={columnFallback(step.translations, language, INSTRUCTIONS, step.instructions)}
                rows={6}
                onChange={(instructions) => { onChange({ ...step, ...(language === "en" ? { instructions } : {}), translations: storedTranslations(step.translations, withColumnText(step.translations, language, INSTRUCTIONS, instructions)) }); }}
              />
              <FieldProblems check={check} path={`translations.${language}.instructions`} />
            </>
          )}
        </BilingualColumns>
        <FieldProblems check={check} path="instructions" />
      </Section>
      <Section title={t("authoring.step.human.inputs")} hint={t("authoring.step.human.inputsHint")}>
        <ul className="space-y-2">
          {Object.entries(step.inputs).map(([name, slot]) => (
            <li key={name} className={WIDE_CONTROL_ROW_CLASS}>
              <span className="font-mono text-sm font-semibold">{name}</span>
              {valueKinds.map((kind) => (
                <OptionLabel key={kind}>
                  <input
                    type="checkbox"
                    checked={slot.accepts.includes(kind)}
                    onChange={(event) => {
                      const accepts = event.target.checked ? [...slot.accepts, kind] : slot.accepts.filter((accepted) => accepted !== kind);
                      if (accepts.length > 0) {
                        onChange(withHumanInput(step, name, { ...slot, accepts }));
                      }
                    }}
                  />
                  <span className="font-mono">{kind}</span>
                </OptionLabel>
              ))}
              <OptionLabel>
                <input type="checkbox" checked={slot.required} onChange={(event) => { onChange(withHumanInput(step, name, { ...slot, required: event.target.checked })); }} />
                {t("authoring.source.required")}
              </OptionLabel>
              <AppButton variant="ghost" size="xs" aria-label={t("authoring.step.human.removeInputNamed", { name })} onClick={() => { onChange(withHumanInput(step, name, null)); }}>
                {t("authoring.step.human.removeInput")}
              </AppButton>
            </li>
          ))}
        </ul>
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (KEY_NAME_PATTERN.test(newInput) && !(newInput in step.inputs)) {
              onChange(withHumanInput(step, newInput, { accepts: [valueKinds[0] ?? "text"], required: true }));
              setNewInput("");
            }
          }}
        >
          <input
            className={`${FORM_COMPACT_CONTROL_CLASS} w-48 font-mono`}
            aria-label={t("authoring.step.human.newInput")}
            placeholder={t("authoring.step.human.newInput")}
            value={newInput}
            onChange={(event) => { setNewInput(event.target.value); }}
          />
          <AppButton type="submit" variant="secondary" size="xs" disabled={!KEY_NAME_PATTERN.test(newInput) || newInput in step.inputs}>
            {t("authoring.step.human.addInput")}
          </AppButton>
        </form>
        <p className={FORM_HELP_TEXT_CLASS}>{t("authoring.step.keyNameHint")}</p>
        <FieldProblems check={check} path="inputs" />
      </Section>
      <Section title={t("authoring.step.human.response")} hint={t("authoring.step.human.responseHint")}>
        <div className="flex flex-wrap gap-4" role="radiogroup" aria-label={t("authoring.step.human.response")}>
          <OptionLabel>
            <input type="radio" checked={response.kind === "text"} onChange={() => { onChange({ ...step, response: { kind: "text" } }); }} />
            {t("authoring.step.human.responses.text")}
          </OptionLabel>
          <OptionLabel>
            <input type="radio" checked={response.kind === "choice"} onChange={() => { onChange({ ...step, response: { kind: "choice", options: [{ key: "yes", label: t("authoring.step.human.yes") }, { key: "no", label: t("authoring.step.human.no") }] } }); }} />
            {t("authoring.step.human.responses.choice")}
          </OptionLabel>
          <OptionLabel>
            <input
              type="radio"
              checked={response.kind === "review_output"}
              disabled={inputNames.length === 0}
              onChange={() => {
                const first = inputNames[0];
                if (first !== undefined) {
                  onChange({ ...step, response: { kind: "review_output", input: first, comment: "optional" } });
                }
              }}
            />
            {t("authoring.step.human.responses.review_output")}
          </OptionLabel>
        </div>
        {response.kind === "choice" ? (
          <ul className="app-bilingual space-y-1 border-l-2 pl-3">
            {/* The columns' names once where the rows lay out side by side; each field names its own where they stack (pane-layouts.css). */}
            <li className="app-choice-row app-choice-head text-xs" aria-hidden="true">
              <span className="app-label">{t("authoring.step.human.optionKey")}</span>
              {languages.map((language) => <span key={language} className="app-label">{LANGUAGE_NAMES[language]}</span>)}
            </li>
            {response.options.map((option, index) => (
              <li key={index} className="app-choice-row text-xs">
                <Field label={t("authoring.step.human.optionKey")} className="app-choice-cell min-w-0">
                  {(control) => <ChoiceKeyInput id={control.id} step={step} index={index} label={t("authoring.step.human.optionKey")} onChange={onChange} />}
                </Field>
                {languages.map((language) => (
                  <Field key={language} label={LANGUAGE_NAMES[language]} className="app-choice-cell min-w-0">
                    {(control) => (
                      <input
                        {...control}
                        lang={language}
                        className={`${FORM_COMPACT_CONTROL_CLASS} w-full`}
                        aria-label={t("authoring.bilingual.field", { field: t("authoring.step.human.optionLabel"), language: LANGUAGE_NAMES[language] })}
                        value={columnText(step.translations, language, { field: "choice_labels", key: option.key }, option.label)}
                        placeholder={columnFallback(step.translations, language, { field: "choice_labels", key: option.key }, option.label)}
                        onChange={(event) => {
                          const label = event.target.value;
                          const options = language === "en" ? response.options.map((other, position) => (position === index ? { ...other, label } : other)) : response.options;
                          onChange({ ...step, response: { ...response, options }, translations: storedTranslations(step.translations, withColumnText(step.translations, language, { field: "choice_labels", key: option.key }, label)) });
                        }}
                      />
                    )}
                  </Field>
                ))}
                <AppButton variant="ghost" size="xs" disabled={response.options.length <= 1} onClick={() => { onChange({ ...step, response: { ...response, options: response.options.filter((_other, position) => position !== index) }, translations: storedTranslations(step.translations, withChoiceKey(step.translations, option.key, null)) }); }}>
                  {t("authoring.step.human.removeOption")}
                </AppButton>
              </li>
            ))}
            <li>
              <AppButton variant="ghost" size="xs" onClick={() => { onChange({ ...step, response: { ...response, options: [...response.options, { key: `option_${String(response.options.length + 1)}`, label: t("authoring.step.human.newOption") }] } }); }}>
                {t("authoring.step.human.addOption")}
              </AppButton>
            </li>
            {languages.map((language) => <li key={language}><FieldProblems check={check} path={`translations.${language}.choice_labels`} /></li>)}
          </ul>
        ) : null}
        {response.kind === "review_output" ? (
          <div className={NESTED_OPTIONS_CLASS}>
            <Field layout="inline" label={t("authoring.step.human.reviewedInput")}>
              {(control) => (
                <select {...control} className={`${FORM_SELECT_CLASS} w-auto`} value={response.input} onChange={(event) => { onChange({ ...step, response: { ...response, input: event.target.value } }); }}>
                  {inputNames.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
              )}
            </Field>
            {(["optional", "required"] as const).map((rule) => (
              <OptionLabel key={rule}>
                <input type="radio" checked={response.comment === rule} onChange={() => { onChange({ ...step, response: { ...response, comment: rule } }); }} />
                {t(`authoring.step.human.comment.${rule}`)}
              </OptionLabel>
            ))}
          </div>
        ) : null}
        <FieldProblems check={check} path="response" />
      </Section>
    </>
  );
}

/**
 * A choice's key (Sol 4c r1 C2, r2 C6). Each keystroke that makes a valid key no other choice has
 * renames the choice at once, its labels moving from its own key, so the draft (and its unsaved
 * guard) holds every edit that can be saved and a label never lands on another choice's. Text
 * that isn't such a key yet stays in the field, marked, and goes when the field is left.
 */
function ChoiceKeyInput({ id, step, index, label, onChange }: { id: string; step: HumanStepDefinition; index: number; label: string; onChange: (step: HumanStepDefinition) => void }) {
  const current = step.response.kind === "choice" ? (step.response.options[index]?.key ?? "") : "";
  const [unusable, setUnusable] = useState<string | null>(null);
  return (
    <input
      id={id}
      className={`${FORM_COMPACT_CONTROL_CLASS} w-full font-mono`}
      aria-label={label}
      aria-invalid={unusable !== null}
      value={unusable ?? current}
      onChange={(event) => {
        const key = event.target.value;
        if (key !== current && !choiceKeyFree(step, index, key)) {
          setUnusable(key);
          return;
        }
        setUnusable(null);
        if (key !== current) onChange(withChoiceRenamed(step, index, key));
      }}
      onBlur={() => { setUnusable(null); }}
    />
  );
}
